import type { SupabaseClient } from "@supabase/supabase-js";

import type { UseReadModelRealtime } from "./useReadModel";

/** Realtime publication tables wired for Command Center live feeds. */
export type CadRealtimeTable = "checkpoint_scans" | "alert_events" | "route_assignments";

/** dock-* keys feed the CAD dock badges; they must never collide with an open tile's channel. */
export type CadRealtimeSurface =
  | "activity"
  | "alerts"
  | "roster"
  | "status"
  | "dock-roster"
  | "dock-activity";

/** Build an account-scoped Realtime subscription for a CAD read surface. */
export function cadRealtimeFeed(
  client: SupabaseClient,
  accountId: string,
  surface: CadRealtimeSurface,
  table: CadRealtimeTable,
): UseReadModelRealtime {
  return {
    client,
    channel: `cad:${surface}:${accountId}`,
    table,
    accountId,
  };
}

/** Count alerts still in the open state for the status strip. */
export function countOpenAlerts(rows: { status: string }[]): number {
  return rows.filter((row) => row.status === "open").length;
}

/** Count open SOS alerts only — the status strip surfaces these distinctly (PRD-001O AC-001O.2.3). */
export function countOpenSosAlerts(rows: { status: string; alertType: string }[]): number {
  return rows.filter((row) => row.status === "open" && row.alertType === "sos").length;
}

const RECENT_SCAN_WINDOW_MS = 60 * 60 * 1000;

/** Count captures inside the Activity dock badge window (last hour by default). */
export function countRecentScans(
  rows: { serverReceivedAt: string }[],
  nowMs: number,
  windowMs: number = RECENT_SCAN_WINDOW_MS,
): number {
  return rows.filter((row) => {
    const receivedMs = Date.parse(row.serverReceivedAt);
    return Number.isFinite(receivedMs) && nowMs - receivedMs <= windowMs && receivedMs <= nowMs + 60_000;
  }).length;
}

/** Count roster rows currently on shift (assignment active right now, not merely scheduled). */
export function countOnShift(rows: { assignmentStatus: string }[]): number {
  return rows.filter((row) => row.assignmentStatus === "active").length;
}

/**
 * Poll interval for dock badge-only reads (on-shift, activity). Tiles keep `pollMs` when open;
 * badges rely on Realtime plus a slower fallback so the desk is not double-polling full lists
 * at the command-center cadence forever.
 */
export function dockBadgePollMs(consolePollMs: number): number {
  if (!Number.isFinite(consolePollMs) || consolePollMs <= 0) {
    return 45_000;
  }
  return Math.max(consolePollMs * 3, 45_000);
}
